import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ConfigContext from '../../../contexts/ConfigContext';
import PublicEventsContext from './PublicEventsContext';
import { cityState } from '../../../modules/helpers';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Los_Angeles');

function EventMeta({ event, location }) {
  const { locationId, truckId, image, description: contextDescription } = useContext(PublicEventsContext);
  const { url: contextUrl, vehicle_type, city_name, state, imageBase } = useContext(ConfigContext);
  const url = contextUrl;

  let fullImage;
  if (image) {
    fullImage = /^https?:\/\//.test(image) ? image : `${imageBase}/${image}`;
  }

  let description = null;
  let name = null;

  // Event address
  const addressString = location?.address || location?.filtered_address || event.address;
  const locationName = location?.name || addressString || `${city_name}, ${state}`;
  let address = {
    '@type': 'PostalAddress',
    addressLocality: city_name,
    addressRegion: state,
    addressCountry: 'US',
  };
  if (addressString) {
    const cityStateValue = cityState(addressString);
    const [locality, region] = cityStateValue ? cityStateValue.split(',') : [city_name, state];

    address = {
      '@type': 'PostalAddress',
      streetAddress: addressString.trim().split(',')[0].trim(),
      addressLocality: locality.trim(),
      addressRegion: region.trim(),
      addressCountry: 'US',
    };
  }

  // Truck Events
  if (truckId) {
    name = event.display_name;
  }

  // Location Events
  if (locationId) {
    description = `Food ${vehicle_type}s at ${locationName}`;

    if (event.bookings && event.bookings.length > 0) {
      const truckNames = event.bookings.map((booking) => booking.truck.name);
      name = truckNames.join(', ');
    } else {
      name = `${locationName} Food ${vehicle_type}s`;
    }
  }

  const metaJSON = {
    '@context': 'https://schema.org',
    '@type': 'FoodEvent',
    name,
    description: description || contextDescription || undefined,
    startDate: dayjs(event.start_time).tz().format(),
    endDate: dayjs(event.end_time).tz().format(),
    url,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    image: fullImage,
    location: {
      '@type': 'Place',
      name: locationName,
      address,
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(metaJSON)}
      </script>
    </Helmet>
  );
}

EventMeta.propTypes = {
  event: PropTypes.object.isRequired,
  location: PropTypes.object,
};

export default EventMeta;
